import React, { forwardRef, useImperativeHandle, useState } from 'react';
import s from '../../styles/AdminDocumentMap.module.css';

export interface AddRowProps {
  onAdd: (name: string, recurrence: string, multi: string, viewingRole: string, edittingRole: string) => Promise<void>;
}

export interface AddDocumentTypeRowHandle {
  open: () => void;
}

const AddDocumentTypeRow = forwardRef<AddDocumentTypeRowHandle, AddRowProps>(({ onAdd }, ref) => {
  const [isOpen, setIsOpen] = useState(false);
  const [value, setValue] = useState('');
  const [recurrence, setRecurrence] = useState('none');
  const [multi, setMulti] = useState(false);
  const [viewingRole, setViewingRole] = useState('');
  const [edittingRole, setEdittingRole] = useState('');
  const [saving, setSaving] = useState(false);

  useImperativeHandle(ref, () => ({ open: () => setIsOpen(true) }));

  const close = () => { setValue(''); setRecurrence('none'); setMulti(false); setViewingRole(''); setEdittingRole(''); setIsOpen(false); };

  const submit = async () => {
    const trimmed = value.trim();
    if (!trimmed) return;
    setSaving(true);
    await onAdd(trimmed, recurrence, multi ? 'true' : 'false', viewingRole, edittingRole);
    setSaving(false);
    close();
  };

  if (!isOpen) return null;
  return (
    <div className={s.modalOverlay} onClick={close}>
      <div className={s.modal} onClick={e => e.stopPropagation()}>
        <p className={s.modalTitle}>Add Document Type</p>

        <div className={s.modalField}>
          <label className={s.modalLabel}>Document Type Name</label>
          <input
            className={s.modalInput}
            placeholder="Enter document type name..."
            value={value}
            onChange={e => setValue(e.target.value)}
            onKeyDown={e => { if (e.key === 'Enter') submit(); if (e.key === 'Escape') close(); }}
            autoFocus
          />
        </div>

        <div className={s.modalField}>
          <label className={s.modalLabel}>Recurrence</label>
          <select
            className={s.modalSelect}
            value={recurrence}
            onChange={e => setRecurrence(e.target.value)}
          >
            <option value="none">None</option>
            <option value="1_month">1 Month</option>
            <option value="3_month">3 Months</option>
            <option value="1_year">1 Year</option>
            <option value="3_year">3 Years</option>
          </select>
        </div>

        <div className={s.modalField}>
          <label className={s.modalLabel}>Multiple Uploads</label>
          <label className={s.modalCheckboxRow}>
            <input
              type="checkbox"
              checked={multi}
              onChange={e => setMulti(e.target.checked)}
            />
            Allow multiple uploads
          </label>
        </div>

        <div className={s.modalField}>
          <label className={s.modalLabel}>Viewing Role</label>
          <select
            className={s.modalSelect}
            value={viewingRole}
            onChange={e => setViewingRole(e.target.value)}
          >
            <option value="">Select role...</option>
            <option value="agent">Agent</option>
            <option value="manager">Manager</option>
            <option value="admin">Admin</option>
          </select>
        </div>

        <div className={s.modalField}>
          <label className={s.modalLabel}>Editing Role</label>
          <select
            className={s.modalSelect}
            value={edittingRole}
            onChange={e => setEdittingRole(e.target.value)}
          >
            <option value="">Select role...</option>
            <option value="agent">Agent</option>
            <option value="manager">Manager</option>
            <option value="admin">Admin</option>
          </select>
        </div>

        <div className={s.modalActions}>
          <button className={`${s.btnSm} ${s.btnSecondary}`} onClick={close}>Cancel</button>
          <button className={`${s.btnSm} ${s.btnPrimary}`} onClick={submit} disabled={saving || !value.trim() || !viewingRole || !edittingRole}>
            {saving ? '…' : 'Add Document Type'}
          </button>
        </div>
      </div>
    </div>
  );
});

AddDocumentTypeRow.displayName = 'AddDocumentTypeRow';
export default AddDocumentTypeRow;